import { GeometrySpec, GEOMETRY_PRESETS, DEFAULT_FIGURE, FigureSpec, initialParams, quizIssues, readFigureSpec, SOLID_PRESETS, SolidSpec } from "@jange/shared";
import type { Block, ContentBody, Step } from "./api";

export const BLOCK_TYPES = [
  { type: "text", label: "Texte" },
  { type: "formula", label: "Formule" },
  { type: "figure", label: "Courbe (fonction)" },
  { type: "geometry", label: "Figure de géométrie" },
  { type: "image", label: "Schéma ou photo" },
  { type: "mistake", label: "Erreur fréquente" },
  { type: "video", label: "Vidéo (optionnelle)" },
  { type: "solid3d", label: "Solide 3D" },
  { type: "scene3d", label: "Modèle 3D" },
  { type: "quiz", label: "Question rapide" },
] as const;
export type EditableType = (typeof BLOCK_TYPES)[number]["type"];

export const LIMITS = { blocks: 60, steps: 30, idLen: 40 };

/** Identifiant court et lisible, unique dans le contenu (b1, b2… / s1, s2…). */
export function nextId(prefix: string, taken: Iterable<string>) {
  const set = new Set(taken);
  let i = 1;
  while (set.has(`${prefix}${i}`)) i++;
  return `${prefix}${i}`;
}

export function newBlock(type: EditableType, id: string): Block {
  switch (type) {
    case "text": return { id, type, md: "" };
    case "formula": return { id, type, latex: "", display: true };
    case "figure": return { id, type, assetId: "inline", engine: "mafs", spec: structuredClone(DEFAULT_FIGURE) as unknown as Record<string, unknown>, params: {} };
    case "geometry": return { id, type, spec: structuredClone(GEOMETRY_PRESETS[0]!.spec) as unknown as Record<string, unknown>, params: {} };
    case "image": return { id, type, assetId: "", alt: {}, aspect: 4 / 3, markers: [], credit: null };
    case "mistake": return { id, type, md: "" };
    case "video": return { id, type, assetId: "", kind: "TEACHER_INTRO", durationSec: 0, sizeBytes: 0, optional: true };
    case "solid3d": return { id, type, spec: structuredClone(SOLID_PRESETS[0].spec) as unknown as Record<string, unknown> };
    case "scene3d": return { id, type, assetId: "", sizeBytes: 0, parts: [], fallback2dAssetId: null, hotspots: [] };
    case "quiz": return { id, type, question: "", choices: [{ id: "a", label: "" }, { id: "b", label: "" }], answerId: "a", explanation: "" };
  }
}

export const emptyBody = (): ContentBody => ({
  blocks: [{ id: "b1", type: "text", md: "" }],
  steps: [{ id: "s1", title: { fr: "" }, blockIds: ["b1"] }],
});

/** Mêmes règles que le serveur (le serveur reste seul juge) : on prévient avant d'envoyer. */
export function validateBody(b: ContentBody, lang: string): string[] {
  const errs: string[] = [];
  const ids = new Set<string>();
  if (b.blocks.length === 0) errs.push("Ajoutez au moins un bloc.");
  if (b.steps.length === 0) errs.push("Ajoutez au moins une étape.");
  if (b.blocks.length > LIMITS.blocks) errs.push(`${LIMITS.blocks} blocs au plus.`);
  if (b.steps.length > LIMITS.steps) errs.push(`${LIMITS.steps} étapes au plus.`);
  for (const x of b.blocks) {
    if (ids.has(x.id)) errs.push(`Identifiant de bloc en double : ${x.id}`);
    ids.add(x.id);
    if ((x.type === "text" || x.type === "mistake") && !x.md.trim()) errs.push(`Le bloc ${x.id} est vide.`);
    if (x.type === "formula" && !x.latex.trim()) errs.push(`La formule ${x.id} est vide.`);
    if (x.type === "video" && !x.assetId.trim()) errs.push(`Choisissez la vidéo du bloc ${x.id}.`);
    if (x.type === "scene3d" && !x.assetId.trim()) errs.push(`Choisissez le modèle 3D du bloc ${x.id}.`);
    if (x.type === "scene3d") for (const h of x.hotspots) if (!h.label.fr?.trim()) errs.push(`Modèle ${x.id} : un point annoté n'a pas de nom en français.`);
    if (x.type === "quiz") {
      if (!x.question.trim()) errs.push(`Question ${x.id} : l'énoncé est vide.`);
      for (const m of quizIssues(x)) errs.push(`Question ${x.id} : ${m}.`);
    }
    if (x.type === "solid3d") { const r = SolidSpec.safeParse(x.spec); if (!r.success) errs.push(`Solide ${x.id} : ${r.error.issues[0]?.message ?? "invalide"}`); }
    if (x.type === "geometry") { const r = GeometrySpec.safeParse(x.spec); if (!r.success) errs.push(`Figure de géométrie ${x.id} : ${r.error.issues[0]?.message ?? "invalide"}`); }
    if (x.type === "image" && !x.assetId) errs.push(`Image ${x.id} : choisissez une image de la médiathèque`);
    if (x.type === "image" && x.markers.some((m) => !m.label.fr?.trim())) errs.push(`Image ${x.id} : chaque repère a besoin d'un nom en français`);
    if (x.type === "figure" && Object.keys(x.spec).length > 0) {
      const r = FigureSpec.safeParse(x.spec);
      if (!r.success) errs.push(`Figure ${x.id} : ${r.error.issues[0]?.message ?? "invalide"}`);
    }
  }
  const sliders = new Set(b.blocks.flatMap((x) => (x.type === "figure" ? Object.keys(initialParams(readFigureSpec(x.spec)))
    : x.type === "solid3d" ? (SolidSpec.safeParse(x.spec).data?.sliders ?? []).map((s) => s.name)
    : x.type === "geometry" ? (GeometrySpec.safeParse(x.spec).data?.sliders ?? []).map((s) => s.name) : [])));
  b.steps.forEach((s, i) => {
    for (const k of Object.keys(s.figureParams ?? {})) if (!sliders.has(k)) errs.push(`L'étape ${i + 1} règle un curseur supprimé : ${k}`);
    if (!(s.title[lang] ?? s.title.fr ?? "").trim()) errs.push(`L'étape ${i + 1} n'a pas de titre.`);
    if (s.blockIds.length === 0) errs.push(`L'étape ${i + 1} n'affiche aucun bloc.`);
    for (const id of s.blockIds) if (!ids.has(id)) errs.push(`L'étape ${i + 1} référence un bloc supprimé : ${id}`);
  });
  return errs;
}

export function move<T>(xs: T[], i: number, d: -1 | 1): T[] {
  const j = i + d;
  if (j < 0 || j >= xs.length) return xs;
  const out = xs.slice();
  [out[i], out[j]] = [out[j], out[i]];
  return out;
}

/** Retire un bloc et toutes ses références dans les étapes. */
export function removeBlock(b: ContentBody, id: string): ContentBody {
  return { blocks: b.blocks.filter((x) => x.id !== id), steps: b.steps.map((s) => ({ ...s, blockIds: s.blockIds.filter((x) => x !== id) })) };
}

export function updateStep(b: ContentBody, i: number, patch: Partial<Step>): ContentBody {
  return { ...b, steps: b.steps.map((s, k) => (k === i ? { ...s, ...patch } : s)) };
}

// --- Brouillon local : rien n'est perdu si l'onglet se ferme ou si le réseau coupe ---------
type LocalDraft = { body: ContentBody; baseId: string | null; savedAt: number };
const key = (notionId: string, lang: string) => `jange.studio.draft.${notionId}.${lang}`;
export const localDraft = {
  get(notionId: string, lang: string): LocalDraft | null {
    try { const s = localStorage.getItem(key(notionId, lang)); return s ? JSON.parse(s) as LocalDraft : null; } catch { return null; }
  },
  set(notionId: string, lang: string, d: LocalDraft) { try { localStorage.setItem(key(notionId, lang), JSON.stringify(d)); } catch { /* quota ou stockage bloqué */ } },
  clear(notionId: string, lang: string) { try { localStorage.removeItem(key(notionId, lang)); } catch { /* rien */ } },
};

/** Curseurs des figures visibles à une étape (pour régler leur valeur à cette étape). */
export function stepSliders(b: ContentBody, stepIndex: number) {
  const s = b.steps[stepIndex];
  if (!s) return [];
  const seen = new Map<string, FigureSpec["sliders"][number]>();
  for (const id of s.blockIds) {
    const x = b.blocks.find((y) => y.id === id);
    if (x?.type === "figure") for (const sl of readFigureSpec(x.spec).sliders) if (!seen.has(sl.name)) seen.set(sl.name, sl);
    if (x?.type === "solid3d") for (const sl of SolidSpec.safeParse(x.spec).data?.sliders ?? []) if (!seen.has(sl.name)) seen.set(sl.name, sl);
  }
  return [...seen.values()];
}
